import { Link } from 'react-router-dom';
import { useLangPrefix, withLangPrefix } from '../hooks/useLangPrefix';
import {
  Award, MapPin, Users, Compass, Sparkles, Heart, Anchor, Car, Bike, Truck,
  HeartPulse, Tent, Sun, ChevronRight, ArrowRight,
} from 'lucide-react';

// Anciens élèves devenus collaborateurs (réseau STS — source officielle)
const STS_MEMBERS = [
  { name: 'Eric',       region: 'Vaucluse',          dept: '84', icon: Sparkles },
  { name: 'Fred',       region: 'Drôme',             dept: '26', icon: Sparkles },
  { name: 'Christiane', region: 'Var',               dept: '83', icon: Sparkles },
  { name: 'Valérie & Régis', region: 'Manche',       dept: '50', icon: Sparkles },
  { name: 'Bibi',       region: 'Bouches-du-Rhône',  dept: '13', icon: Sparkles },
  { name: 'Valentine',  region: 'Bouches-du-Rhône',  dept: '13', icon: Sparkles },
];

const STS_PRESTATIONS = [
  { label: 'Sellerie automobile',        icon: Car },
  { label: 'Sellerie nautique',          icon: Anchor },
  { label: 'Sellerie moto',              icon: Bike },
  { label: 'Domaine médical',            icon: HeartPulse },
  { label: 'Poids lourds',               icon: Truck },
  { label: 'Camping-car & caravaning',   icon: Tent },
  { label: 'Collectivités',              icon: Users },
  { label: 'Agencement de terrasses',    icon: Sun },
];

const STS_HERITAGE = [
  'Compagnons du Devoir',
  'TJL · La Sellerie Automobile',
  'CTAI',
  'SA Borguino',
  'Expertise nautique',
];

export const ReseauSTSPage = () => {
  const { lang } = useLangPrefix();
  const L = (p) => withLangPrefix(p, lang);

  return (
    <div className="min-h-screen bg-[#FAF7F0]" data-testid="reseau-sts-page">
      {/* ─────────── HERO ─────────── */}
      <section className="relative bg-[#1F2A4A] text-white overflow-hidden">
        {/* Subtle grain */}
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-24 md:pt-40 md:pb-32">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/60 mb-10" aria-label="Fil d'Ariane">
            <Link to={L('/')} className="hover:text-[#C9B896] transition-colors">Accueil</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white">Le réseau STS</span>
          </nav>

          <div className="grid grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="col-span-12 md:col-span-7">
              <h1 className="font-light text-3xl md:text-5xl lg:text-[5.5rem] leading-[1.08] mb-7">
                <span className="block">Le réseau <span className="font-serif italic text-[#C9B896]">STS.</span></span>
                <span className="block font-serif italic text-white">La Sellerie<br />Tout Simplement.</span>
              </h1>
              <p className="text-lg md:text-xl text-white/85 font-light leading-relaxed max-w-2xl mb-10">
                Nos élèves d'aujourd'hui deviennent nos collaborateurs de demain. Plus de 20 années d'expérience, un savoir-faire unique, une seule bannière.
              </p>
              <div className="flex flex-wrap items-center gap-3 text-xs font-mono uppercase tracking-[0.22em] text-white/80">
                <span className="px-4 py-2 rounded-full border border-white/15 bg-white/5">+ 20 ans d'expérience</span>
                <span className="px-4 py-2 rounded-full border border-white/15 bg-white/5">France métropolitaine · DOM-TOM</span>
                <span className="px-4 py-2 rounded-full border border-white/15 bg-white/5">Pays francophones</span>
              </div>
            </div>

            <div className="col-span-12 md:col-span-5">
              <div className="relative">
                <div className="aspect-square rounded-2xl bg-white/95 flex items-center justify-center p-12 shadow-2xl shadow-black/30">
                  <img
                    src="/sa/STSS-removebg-preview-1.png"
                    alt="Logo STS — La Sellerie Tout Simplement"
                    className="w-full h-full object-contain"
                    data-testid="sts-logo-hero"
                  />
                </div>
                <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 px-5 py-2.5 rounded-full bg-[#C9B896] text-[#0F1623] text-[10px] uppercase tracking-[0.3em] font-medium shadow-xl whitespace-nowrap">
                  Sellerie Académie · membre fondateur
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── 01 · FONDEMENTS ─────────── */}
      <section className="py-12 md:py-16 bg-[#FAF7F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-12 gap-10 lg:gap-20">
            <div className="col-span-12 md:col-span-4">
              <h2 className="font-light text-2xl md:text-4xl leading-tight text-[#1F2A4A]">
                Un savoir-faire <span className="font-serif italic">unique.</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-8 space-y-6 text-[#1F2A4A]/90 text-lg font-light leading-relaxed">
              <p>
                STS, La Sellerie Tout Simplement, tire sa force d'un savoir-faire unique acquis au fil de
                plus de <strong className="text-[#1F2A4A] font-medium">20 années d'expérience</strong>. Nous avons créé le premier réseau d'artisans
                selliers-garnisseurs de France, unis sous une seule bannière&nbsp;: <em className="font-serif italic">la Sellerie Tout Simplement</em>.
              </p>
              <p>
                STS, c'est avant tout une <strong className="text-[#1F2A4A] font-medium">passion pour la sellerie</strong>, ainsi que la transmission de notre
                savoir-faire artisanal dans sa forme la plus authentique. Nous aspirons à unifier les artisans au
                sein de notre réseau après les avoir formés grâce à notre propre programme de formations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stitched separator */}
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <svg width="100%" height="2" viewBox="0 0 1200 2" preserveAspectRatio="none">
          <line x1="0" y1="1" x2="1200" y2="1" stroke="#C9B896" strokeWidth="1" strokeDasharray="4 4" />
        </svg>
      </div>

      {/* ─────────── 02 · HÉRITAGE ─────────── */}
      <section className="py-12 md:py-16 bg-[#FAF7F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-light text-2xl md:text-4xl text-[#1F2A4A] leading-tight">
              Forts d'un héritage <span className="font-serif italic">ancestral.</span>
            </h2>
            <p className="mt-5 text-[#1F2A4A]/80 text-base md:text-lg font-light leading-relaxed">
              Notre expertise puise dans les plus grandes écoles et entreprises de la sellerie française.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-5">
            {STS_HERITAGE.map((h, i) => (
              <div
                key={i}
                className="aspect-square flex items-center justify-center p-5 rounded-2xl bg-white border border-[#1F2A4A]/8 hover:border-[#C9B896]/60 hover:shadow-xl transition-all duration-500 text-center group"
                data-testid={`heritage-${i}`}
              >
                <div>
                  <div className="w-9 h-9 mx-auto mb-3 rounded-full bg-[#C9B896]/15 group-hover:bg-[#C9B896] flex items-center justify-center transition-all">
                    <Award className="w-4 h-4 text-[#C9B896] group-hover:text-[#0F1623] transition-colors" />
                  </div>
                  <p className="text-[#1F2A4A] text-sm md:text-[15px] font-medium leading-snug">{h}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── 03 · PRESTATIONS ─────────── */}
      <section className="py-12 md:py-16 bg-[#1F2A4A] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-12 gap-10 mb-14">
            <div className="col-span-12 md:col-span-7">
              <h2 className="font-light text-2xl md:text-4xl leading-tight">
                Une <span className="font-serif italic">gamme complète</span><br />
                pour chaque univers de sellerie.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-5 md:pt-4">
              <p className="text-white/85 text-lg font-light leading-relaxed">
                Nos créations sur-mesure assurent un <strong className="text-white font-medium">confort optimal</strong> et sont disponibles dans toute la France métropolitaine, les DOM-TOM et les pays francophones.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
            {STS_PRESTATIONS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={i}
                  className="group p-6 md:p-7 rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] hover:border-[#C9B896]/40 transition-all duration-500"
                  data-testid={`prestation-${i}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-[#C9B896]/15 group-hover:bg-[#C9B896] flex items-center justify-center mb-4 transition-all duration-500">
                    <Icon className="w-5 h-5 text-[#C9B896] group-hover:text-[#0F1623] transition-colors" />
                  </div>
                  <p className="text-[15px] md:text-base font-medium text-white">{p.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─────────── 04 · MEMBRES ─────────── */}
      <section className="py-12 md:py-16 bg-[#FAF7F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-12 gap-10 mb-14 items-end">
            <div className="col-span-12 md:col-span-8">
              <h2 className="font-light text-2xl md:text-4xl text-[#1F2A4A] leading-tight">
                Nos élèves d'aujourd'hui<br />
                <span className="font-serif italic">deviennent nos collaborateurs.</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-4 md:text-right">
              <p className="text-[#1F2A4A]/75 text-base font-light leading-relaxed">
                Six artisans formés ici, désormais membres officiels du réseau STS — répartis sur tout le territoire.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {STS_MEMBERS.map((m, i) => (
              <div
                key={i}
                className="group relative flex items-center gap-5 p-6 rounded-2xl bg-white border border-[#1F2A4A]/8 hover:border-[#C9B896]/60 hover:shadow-xl transition-all duration-500"
                data-testid={`sts-member-${i}`}
              >
                <div className="shrink-0 w-14 h-14 rounded-full bg-gradient-to-br from-[#C9B896] to-[#A89668] flex items-center justify-center text-[#0F1623] font-serif italic text-2xl shadow-md">
                  {m.name.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[#1F2A4A] font-medium text-lg leading-tight">{m.name}</p>
                  <p className="text-[#1F2A4A]/75 text-sm flex items-center gap-1.5 mt-1">
                    <MapPin className="w-3.5 h-3.5 text-[#C9B896] shrink-0" />
                    {m.region}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── 05 · AMBITION ─────────── */}
      <section className="py-12 md:py-16 bg-[#F4EFE3]/40">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Heart className="w-9 h-9 text-[#C9B896] mx-auto mb-6" />
          <h2 className="font-light text-2xl md:text-4xl text-[#1F2A4A] leading-tight mb-7">
            Une <span className="font-serif italic">passion</span> transmise.<br />
            Un <span className="font-serif italic">service</span> prestigieux.
          </h2>
          <p className="text-[#1F2A4A]/85 text-lg md:text-xl font-light leading-relaxed">
            Nous sommes animés par une <strong className="text-[#1F2A4A] font-medium">ambition sans pareille dans notre métier</strong>&nbsp;: vous offrir un service prestigieux, de qualité, répondant aux plus hauts critères de satisfaction.
          </p>

          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to={L('/formations-sur-place')}
              className="inline-flex items-center gap-3 bg-[#1F2A4A] text-[#FAF7F0] hover:bg-[#324166] rounded-full px-8 py-4 text-sm uppercase tracking-[0.25em] font-medium shadow-xl shadow-[#1F2A4A]/15 transition-all hover:gap-5 group"
              data-testid="sts-cta-formations"
            >
              Rejoindre le réseau par la formation
              <ArrowRight className="w-4 h-4 text-[#C9B896]" />
            </Link>
            <Link
              to={L('/contact')}
              className="inline-flex items-center gap-2 text-[#1F2A4A] hover:text-[#C9B896] text-sm uppercase tracking-[0.25em] underline-offset-4 hover:underline transition-colors"
              data-testid="sts-cta-contact"
            >
              Contacter le réseau →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReseauSTSPage;
